import { useLocalStorageState } from "@istari-frontend/shared";
import { useLayoutEffect } from "react";

import type { ColorThemeId } from "../model/color-theme";
import { FALLBACK_COLOR_THEME_ID, isColorThemeId } from "../model/color-themes";

export const COLOR_THEME_STORAGE_KEY = "istari-color-theme-preference";

function migrateLegacyColorThemeStorage(): void {
  if (typeof window === "undefined") return;
  let raw: string | null;
  try {
    raw = window.localStorage.getItem(COLOR_THEME_STORAGE_KEY);
  } catch {
    return;
  }
  if (raw === null) return;
  try {
    JSON.parse(raw);
  } catch {
    try {
      if (isColorThemeId(raw)) {
        window.localStorage.setItem(COLOR_THEME_STORAGE_KEY, JSON.stringify(raw));
      } else {
        window.localStorage.removeItem(COLOR_THEME_STORAGE_KEY);
      }
    } catch {
      return;
    }
  }
}

if (typeof window !== "undefined") {
  migrateLegacyColorThemeStorage();
}

function normalizeColorTheme(stored: unknown): ColorThemeId {
  return isColorThemeId(stored) ? stored : FALLBACK_COLOR_THEME_ID;
}

export function useColorTheme() {
  const [stored, setColorTheme] = useLocalStorageState<ColorThemeId>(COLOR_THEME_STORAGE_KEY, FALLBACK_COLOR_THEME_ID);
  const colorTheme = normalizeColorTheme(stored);

  useLayoutEffect(() => {
    document.documentElement.dataset.colorTheme = colorTheme;
  }, [colorTheme]);

  useLayoutEffect(() => {
    if (stored !== colorTheme) {
      setColorTheme(colorTheme);
    }
  }, [stored, colorTheme, setColorTheme]);

  return { colorTheme, setColorTheme };
}
